import { useEffect, useState } from "react";
import { Loader2, LifeBuoy, Mail, MessageCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

interface Ticket {
  id: string;
  subject: string;
  body: string;
  status: string;
  admin_reply: string | null;
  admin_replied_at: string | null;
  created_at: string;
}

const STATUS_LABEL: Record<string, string> = {
  open: "Aberto",
  pending: "Aguardando você",
  answered: "Respondido",
  closed: "Encerrado",
};

export default function Suporte() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("support_tickets")
      .select("id, subject, body, status, admin_reply, admin_replied_at, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    setTickets((data ?? []) as Ticket[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !subject.trim() || !body.trim()) return;
    setSending(true);
    const { error } = await supabase.from("support_tickets").insert({
      user_id: user.id,
      user_email: user.email ?? null,
      user_name: (user.user_metadata?.full_name as string) ?? null,
      subject: subject.trim(),
      body: body.trim(),
      status: "open",
    });
    setSending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Chamado enviado! Responderemos em breve.");
    setSubject("");
    setBody("");
    load();
  };

  return (
    <div className="space-y-6 px-4 py-8 sm:px-6 lg:px-10">
      <header className="space-y-1">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Conta · Suporte
        </p>
        <h1 className="font-serif text-3xl font-light text-foreground sm:text-4xl">
          Central de atendimento
        </h1>
        <p className="text-sm text-muted-foreground">
          Fale com nossa equipe pelo chat interno ou por e-mail. Respondemos em até 24 h úteis.
        </p>
      </header>

      <Card className="max-w-3xl border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-serif text-lg font-light">
            <Mail className="h-4 w-4 text-primary" /> E-mail direto
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Prefere e-mail?{" "}
          <a
            href="mailto:atendimento@lototerminal.com.br"
            className="text-primary underline"
          >
            atendimento@lototerminal.com.br
          </a>
        </CardContent>
      </Card>

      <Card className="max-w-3xl border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-serif text-lg font-light">
            <LifeBuoy className="h-4 w-4 text-primary" /> Abrir novo chamado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="subj">Assunto</Label>
              <Input
                id="subj"
                value={subject}
                maxLength={120}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="body">Mensagem</Label>
              <Textarea
                id="body"
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
              />
            </div>
            <Button type="submit" disabled={sending}>
              {sending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Enviar chamado
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="max-w-3xl border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-serif text-lg font-light">
            <MessageCircle className="h-4 w-4 text-primary" /> Meus chamados
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Você ainda não abriu nenhum chamado.
            </p>
          ) : (
            tickets.map((t) => (
              <div
                key={t.id}
                className="rounded-md border border-lt-border bg-background p-3 text-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="font-medium text-foreground">{t.subject}</div>
                  <span className="rounded-full border border-lt-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {STATUS_LABEL[t.status] ?? t.status}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Aberto em{" "}
                  {new Date(t.created_at).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{t.body}</p>
                {t.admin_reply && (
                  <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-2">
                    <div className="mb-1 text-[11px] uppercase tracking-wider text-primary">
                      Resposta da equipe
                      {t.admin_replied_at &&
                        ` · ${new Date(t.admin_replied_at).toLocaleDateString("pt-BR")}`}
                    </div>
                    <p className="whitespace-pre-wrap text-foreground">{t.admin_reply}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}